import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle
} from './ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Currency helper (DZD, RTL-aware)
const fmt = (n) =>
  new Intl.NumberFormat('fr-DZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    .format(Number(n || 0));

export default function SeasonReset() {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Form state for closing a season
  const [labelGregorian, setLabelGregorian] = useState('2024-2025');
  const [labelHijri, setLabelHijri] = useState('1446هـ');
  // Season start date: defaults to today. The user can pick any past or
  // future date — everything before this date goes to the old season.
  const todayISO = new Date().toISOString().slice(0, 10);
  const [seasonStartDate, setSeasonStartDate] = useState(todayISO);
  const [archiveOld, setArchiveOld] = useState(false);
  const [notes, setNotes] = useState('');

  // Confirmation modal
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmPhrase, setConfirmPhrase] = useState('');
  const [closing, setClosing] = useState(false);

  // History
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const authHeader = useCallback(() => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  }, []);

  const loadPreview = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const url = seasonStartDate
        ? `${API}/admin/season/preview?season_start_date=${encodeURIComponent(seasonStartDate)}`
        : `${API}/admin/season/preview`;
      const res = await axios.get(url, { headers: authHeader() });
      setPreview(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [authHeader, seasonStartDate]);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await axios.get(`${API}/admin/season/list`, { headers: authHeader() });
      setHistory(res.data.items || []);
    } catch (e) {
      // history is optional — surface as toast, not blocker
      console.warn('season history load:', e);
    } finally {
      setHistoryLoading(false);
    }
  }, [authHeader]);

  useEffect(() => { loadPreview(); loadHistory(); }, [loadPreview, loadHistory]);

  const expectedPhrase = `إغلاق الموسم ${labelGregorian}`;
  const phraseMatches = confirmPhrase.trim() === expectedPhrase;

  const doClose = async () => {
    if (!phraseMatches) return;
    setClosing(true);
    try {
      const res = await axios.post(
        `${API}/admin/season/close`,
        {
          label_gregorian: labelGregorian,
          label_hijri: labelHijri || null,
          archive_old_ops: archiveOld,
          confirmation_phrase: confirmPhrase,
          season_start_date: seasonStartDate || null,
          notes: notes || null,
        },
        { headers: authHeader() }
      );
      toast.success(res.data?.message || 'تم إغلاق الموسم بنجاح');
      setShowConfirm(false);
      setConfirmPhrase('');
      await loadPreview();
      await loadHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'فشل إغلاق الموسم');
    } finally {
      setClosing(false);
    }
  };

  const doRollback = async (snapshotId) => {
    if (!window.confirm('هل أنت متأكد من التراجع عن هذا الإغلاق؟ سيتم استرجاع كل الأرصدة.')) return;
    try {
      const res = await axios.post(
        `${API}/admin/season/rollback/${snapshotId}`,
        {},
        { headers: authHeader() }
      );
      toast.success(res.data?.message || 'تم التراجع');
      await loadPreview();
      await loadHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'فشل التراجع');
    }
  };

  if (loading) {
    return (
      <div className="p-8" dir="rtl">
        <div className="animate-pulse h-32 bg-gray-100 rounded-lg"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8" dir="rtl">
        <Card className="border-red-300 bg-red-50">
          <CardContent className="p-6 text-red-700">
            <p className="font-semibold">تعذّر تحميل المعاينة</p>
            <p className="text-sm mt-2">{error}</p>
            {error.includes('403') || error.toLowerCase().includes('forbidden') ? (
              <p className="text-sm mt-3">هذه الصفحة متاحة للمدير العام فقط (Super Admin).</p>
            ) : null}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" dir="rtl" data-testid="season-reset-page">
      <header className="flex items-center justify-between border-b pb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">🔄 إغلاق الموسم وبدء موسم جديد</h1>
          <p className="text-sm text-gray-600 mt-1">
            تصفير أرصدة الوكالات والخزينة مع حفظ كامل التاريخ المالي.
          </p>
        </div>
      </header>

      {/* KPI summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-5">
            <div className="text-sm text-gray-600">عدد الوكالات</div>
            <div className="text-3xl font-bold text-gray-900 mt-1">{preview?.agencies_count ?? 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <div className="text-sm text-gray-600">إجمالي الرصيد الذي سيُصفَّر</div>
            <div className="text-3xl font-bold text-brand-700 mt-1">
              {fmt(preview?.total_balance_to_zero_out)} دج
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <div className="text-sm text-gray-600">عدد المواسم المُغلقة سابقاً</div>
            <div className="text-3xl font-bold text-gray-900 mt-1">{history.length}</div>
          </CardContent>
        </Card>
      </div>

      {/* Per-agency table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">معاينة الإغلاق — الرصيد الحالي لكل وكالة</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-right">الوكالة</th>
                  <th className="px-4 py-3 text-right">الإيرادات</th>
                  <th className="px-4 py-3 text-right">المُحوَّل</th>
                  <th className="px-4 py-3 text-right">المصاريف</th>
                  <th className="px-4 py-3 text-right">الرصيد الحالي</th>
                  <th className="px-4 py-3 text-right">قيد الإغلاق</th>
                  <th className="px-4 py-3 text-right">عمليات</th>
                </tr>
              </thead>
              <tbody>
                {preview?.agencies?.map((a) => (
                  <tr
                    key={a.agency_id}
                    className="border-b hover:bg-brand-50/40"
                    data-testid={`agency-row-${a.agency_id}`}
                  >
                    <td className="px-4 py-3 font-medium">{a.agency_name}</td>
                    <td className="px-4 py-3 tabular-nums">{fmt(a.revenue)}</td>
                    <td className="px-4 py-3 tabular-nums">{fmt(a.transferred)}</td>
                    <td className="px-4 py-3 tabular-nums">{fmt(a.expenses)}</td>
                    <td className="px-4 py-3 tabular-nums font-semibold">{fmt(a.current_balance)}</td>
                    <td className={`px-4 py-3 tabular-nums font-bold ${a.closing_entry_amount < 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                      {fmt(a.closing_entry_amount)}
                    </td>
                    <td className="px-4 py-3 text-gray-500">{a.operations_to_tag}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Form */}
      <Card className="border-brand-200">
        <CardHeader>
          <CardTitle className="text-lg">إعدادات الإغلاق</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="label-greg">الموسم بالميلادي *</Label>
              <Input
                id="label-greg"
                value={labelGregorian}
                onChange={(e) => setLabelGregorian(e.target.value)}
                placeholder="2024-2025"
                data-testid="season-label-greg-input"
              />
            </div>
            <div>
              <Label htmlFor="label-hijri">الموسم بالهجري (اختياري)</Label>
              <Input
                id="label-hijri"
                value={labelHijri}
                onChange={(e) => setLabelHijri(e.target.value)}
                placeholder="1446هـ"
                data-testid="season-label-hijri-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="season-start-date">
                📅 تاريخ بداية الموسم الجديد
              </Label>
              <Input
                id="season-start-date"
                type="date"
                value={seasonStartDate}
                onChange={(e) => setSeasonStartDate(e.target.value)}
                data-testid="season-start-date-input"
              />
              <p className="text-xs text-gray-600 mt-1">
                كل عملية قبل هذا التاريخ تذهب للموسم القديم. أي عملية في هذا
                التاريخ أو بعده تنتمي للموسم الجديد. الافتراضي: اليوم.
              </p>
              {seasonStartDate && seasonStartDate < todayISO && (
                <p className="text-xs text-amber-700 mt-1 bg-amber-50 px-2 py-1 rounded">
                  ⚠️ اخترت تاريخاً سابقاً — العمليات بين {seasonStartDate} واليوم
                  ستبقى ضمن الموسم الجديد (غير موسومة).
                </p>
              )}
              {seasonStartDate && seasonStartDate > todayISO && (
                <p className="text-xs text-blue-700 mt-1 bg-blue-50 px-2 py-1 rounded">
                  💡 اخترت تاريخاً مستقبلياً — العمليات من اليوم إلى {seasonStartDate}
                  ستذهب للموسم القديم بمجرد ضغط الإغلاق.
                </p>
              )}
            </div>
            <div className="flex items-end">
              <Button
                type="button"
                variant="outline"
                onClick={loadPreview}
                disabled={loading}
                data-testid="season-refresh-preview-btn"
              >
                🔄 تحديث المعاينة حسب التاريخ
              </Button>
            </div>
          </div>

          <div className="flex items-start gap-3 p-4 rounded-md bg-amber-50 border border-amber-200">
            <Switch
              checked={archiveOld}
              onCheckedChange={setArchiveOld}
              id="archive-old"
              data-testid="season-archive-old-switch"
            />
            <div>
              <Label htmlFor="archive-old" className="font-semibold text-amber-900">
                أرشفة العمليات القديمة تلقائياً
              </Label>
              <p className="text-xs text-amber-700 mt-1">
                إذا فُعِّل، ستختفي العمليات القديمة من القوائم الافتراضية وتظهر في صفحة «الأرشيف الذكي».
                التاريخ يبقى محفوظاً ويمكن استرجاعه في أي وقت.
              </p>
            </div>
          </div>

          <div>
            <Label htmlFor="notes">ملاحظات (اختياري)</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="سبب الإغلاق، تاريخ بدء الموسم الجديد، إلخ"
              data-testid="season-notes-input"
            />
          </div>

          <Button
            className="bg-red-600 hover:bg-red-700 text-white"
            onClick={() => setShowConfirm(true)}
            disabled={!labelGregorian}
            data-testid="season-open-confirm-btn"
          >
            🔄 إغلاق الموسم وبدء موسم جديد
          </Button>
        </CardContent>
      </Card>

      {/* History */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">المواسم المُغلقة سابقاً</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {historyLoading ? (
            <div className="p-6 text-center text-gray-500">جاري التحميل…</div>
          ) : history.length === 0 ? (
            <div className="p-6 text-center text-gray-500">لا توجد مواسم مغلقة بعد.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-right">الموسم</th>
                    <th className="px-4 py-3 text-right">تاريخ الإغلاق</th>
                    <th className="px-4 py-3 text-right">من قام</th>
                    <th className="px-4 py-3 text-right">قيود الإغلاق</th>
                    <th className="px-4 py-3 text-right">الحالة</th>
                    <th className="px-4 py-3 text-right">إجراء</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((h) => (
                    <tr key={h.id} className="border-b hover:bg-gray-50" data-testid={`season-history-${h.id}`}>
                      <td className="px-4 py-3 font-medium">{h.label}</td>
                      <td className="px-4 py-3 text-gray-600">{(h.created_at || '').slice(0, 19)}</td>
                      <td className="px-4 py-3 text-gray-600">{h.created_by_name}</td>
                      <td className="px-4 py-3">{h.closing_entries?.length || 0}</td>
                      <td className="px-4 py-3">
                        <Badge variant={h.status === 'active' ? 'default' : 'secondary'}>
                          {h.status === 'active' ? 'نشط' : 'تم التراجع'}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        {h.status === 'active' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => doRollback(h.id)}
                            data-testid={`season-rollback-btn-${h.id}`}
                          >
                            ⏪ تراجع
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Confirmation Modal */}
      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent className="max-w-lg" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-red-700">⚠️ تأكيد إغلاق الموسم</DialogTitle>
            <DialogDescription className="text-gray-700 pt-2 space-y-2">
              <p>سيتم تنفيذ ما يلي على <strong>{preview?.agencies_count}</strong> وكالة:</p>
              <ul className="list-disc pr-5 space-y-1 text-sm">
                <li>تصفير الأرصدة بإجمالي <strong>{fmt(preview?.total_balance_to_zero_out)} دج</strong></li>
                <li>تعليم كل العمليات السابقة بـ <strong>«{labelGregorian}{labelHijri ? ` (${labelHijri})` : ''}»</strong></li>
                <li>الموسم الجديد يبدأ من: <strong>{seasonStartDate || 'اليوم'}</strong></li>
                {archiveOld && <li>أرشفة العمليات القديمة تلقائياً</li>}
                <li>إنشاء snapshot قابل للتراجع خلال 24 ساعة</li>
              </ul>
              <p className="font-semibold text-red-700 pt-3">
                اكتب العبارة التالية حرفياً للتأكيد:
              </p>
              <p className="font-mono text-sm bg-gray-100 px-3 py-2 rounded select-all" dir="rtl">
                {expectedPhrase}
              </p>
            </DialogDescription>
          </DialogHeader>
          <div>
            <Input
              value={confirmPhrase}
              onChange={(e) => setConfirmPhrase(e.target.value)}
              placeholder="اكتب عبارة التأكيد هنا"
              className={phraseMatches ? 'border-emerald-500' : ''}
              data-testid="season-confirm-phrase-input"
              autoFocus
            />
            {confirmPhrase.length > 0 && !phraseMatches && (
              <p className="text-xs text-red-600 mt-1">العبارة غير مطابقة</p>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => { setShowConfirm(false); setConfirmPhrase(''); }}
              disabled={closing}
              data-testid="season-cancel-btn"
            >
              إلغاء
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={doClose}
              disabled={!phraseMatches || closing}
              data-testid="season-confirm-close-btn"
            >
              {closing ? 'جاري الإغلاق…' : '✅ تأكيد وإغلاق الموسم'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
